/**
 * Motor de una llamada de voz con un agente. Junta tres cosas que el navegador
 * hace por separado:
 *  - Captura del micrófono y conversión a PCM16 a 16 kHz (lo que espera Gemini).
 *  - El WebSocket con el backend, que hace de puente con Gemini Live.
 *  - Reproducción del audio que devuelve (PCM16 a 24 kHz), encolado para que no
 *    se solape ni deje huecos.
 *
 * No depende de Solid: expone callbacks y lo envuelve un componente aparte.
 */

const INPUT_SAMPLE_RATE = 16000
const OUTPUT_SAMPLE_RATE = 24000

/** Cuántas muestras se acumulan antes de mandar un trozo al backend. */
const CAPTURE_CHUNK = 2048

/**
 * El procesador que corre en el hilo de audio.
 *
 * Va como texto y se carga desde un Blob en vez de como archivo suelto: un
 * `.js` en `public/` no viajaría con la librería cuando se publica
 * (`build:lib`), y quien la instalara se quedaría sin voz sin saber por qué.
 *
 * `process()` recibe bloques de 128 muestras. Mandarlas de una en una serían
 * 125 mensajes por segundo, así que se acumulan hasta `chunkSize` antes de
 * cruzar al hilo principal.
 */
const CAPTURE_WORKLET = `
class PcmCaptureProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super()
    this.size = options.processorOptions.chunkSize
    this.buffer = new Float32Array(this.size)
    this.offset = 0
  }

  process(inputs) {
    const input = inputs[0] && inputs[0][0]
    // Sin entrada todavía: devolver false mataría el nodo para siempre.
    if (!input) return true

    for (let i = 0; i < input.length; i++) {
      this.buffer[this.offset++] = input[i]
      if (this.offset === this.size) {
        // Copia: el buffer se reutiliza en el bloque siguiente.
        const trozo = this.buffer.slice(0)
        this.port.postMessage(trozo, [trozo.buffer])
        this.offset = 0
      }
    }
    return true
  }
}
registerProcessor('pcm-capture', PcmCaptureProcessor)
`

export type VoiceCallStatus = 'idle' | 'connecting' | 'live' | 'ended' | 'error'

export interface VoiceCallEvents {
  onStatus?: (status: VoiceCallStatus, detail?: string) => void
  /** Transcripción parcial de lo que se dijo (usuario o agente). */
  onTranscript?: (role: 'user' | 'assistant', text: string) => void
  /** El agente empezó a hablar / dejó de hablar: para animar el UI. */
  onSpeakingChange?: (speaking: boolean) => void
}

/** Float32 [-1,1] -> Int16 PCM. */
function floatToPcm16(input: Float32Array): ArrayBuffer {
  const out = new Int16Array(input.length)
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]))
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff
  }
  return out.buffer
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  let binary = ''
  const bytes = new Uint8Array(buf)
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

/** Saca la frecuencia de un MIME tipo `audio/pcm;rate=24000`. */
function parseRate(format?: string): number | null {
  const match = format?.match(/rate=(\d+)/)
  if (!match) return null
  const rate = Number(match[1])
  // Fuera de lo que acepta la Web Audio API no sirve: mejor caer al valor por defecto.
  return rate >= 8000 && rate <= 96000 ? rate : null
}

function base64ToInt16(b64: string): Int16Array {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Int16Array(bytes.buffer)
}

export class VoiceCall {
  private ws?: WebSocket
  private micStream?: MediaStream
  private inputCtx?: AudioContext
  private capture?: AudioWorkletNode
  /** Sólo si el navegador no soporta AudioWorklet. */
  private legacyProcessor?: ScriptProcessorNode
  private workletUrl?: string
  private sink?: GainNode
  private source?: MediaStreamAudioSourceNode

  private outputCtx?: AudioContext
  /** Momento hasta el que ya hay audio programado: el siguiente trozo va detrás. */
  private playhead = 0
  private scheduled: AudioBufferSourceNode[] = []

  private status: VoiceCallStatus = 'idle'

  constructor(
    private url: string,
    private events: VoiceCallEvents = {}
  ) {}

  private setStatus(status: VoiceCallStatus, detail?: string) {
    this.status = status
    this.events.onStatus?.(status, detail)
  }

  /**
   * Despierta un contexto suspendido. La política de reproducción automática de
   * los navegadores suspende cualquier AudioContext que no nazca de una acción
   * del usuario, y suspendido no captura ni reproduce: sólo encola.
   */
  private async resumeContext(ctx: AudioContext): Promise<void> {
    if (ctx.state !== 'suspended') return
    try {
      await ctx.resume()
    } catch {
      /* si no se puede, el estado lo delata más abajo */
    }
  }

  async start() {
    this.setStatus('connecting')
    try {
      // El micrófono primero: si el usuario deniega el permiso, no abrimos el WS.
      this.micStream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } })
    } catch {
      this.setStatus('error', 'Sin acceso al micrófono')
      return
    }

    // El contexto de salida se crea AQUÍ y no al llegar el primer trozo de audio.
    // Un AudioContext creado fuera del gesto del usuario nace suspendido: su
    // reloj no avanza, así que los trozos se programaban contra un `currentTime`
    // congelado y se acumulaban en silencio. Desde fuera se veía justo eso —
    // "conecta, llegan chunks y no suena nada".
    this.outputCtx = new AudioContext({ sampleRate: OUTPUT_SAMPLE_RATE })
    await this.resumeContext(this.outputCtx)

    this.ws = new WebSocket(this.url)
    this.ws.onopen = () => {
      void this.startCapture()
    }
    this.ws.onmessage = e => this.handleMessage(String(e.data))
    this.ws.onerror = () => this.setStatus('error', 'Error de conexión')
    this.ws.onclose = () => {
      if (this.status !== 'error') this.setStatus('ended')
      this.teardownAudio()
    }
  }

  /** Manda al backend un trozo de micrófono ya convertido a PCM16. */
  private sendChunk(samples: Float32Array) {
    if (this.ws?.readyState !== WebSocket.OPEN) return
    const pcm = floatToPcm16(samples)
    this.ws.send(JSON.stringify({ type: 'audio', data: arrayBufferToBase64(pcm), format: `audio/pcm;rate=${INPUT_SAMPLE_RATE}` }))
  }

  /**
   * Arranca la captura del micro y la manda al backend en trozos PCM16.
   *
   * Con AudioWorklet la conversión ocurre en el hilo de audio: con el viejo
   * ScriptProcessorNode todo pasaba por el hilo principal, así que cualquier
   * render pesado de la interfaz se oía como cortes en la voz. Además está
   * deprecado y los navegadores acabarán quitándolo.
   */
  private async startCapture() {
    if (!this.micStream) return
    // El contexto a 16 kHz hace el remuestreo: el micro suele venir a 48 kHz.
    const ctx = new AudioContext({ sampleRate: INPUT_SAMPLE_RATE })
    this.inputCtx = ctx
    // Suspendido no llega ni una muestra del micro: el agente hablaría solo.
    await this.resumeContext(ctx)
    if (ctx.state !== 'running') {
      this.setStatus('error', 'El navegador bloqueó el audio. Vuelve a pulsar para llamar.')
      return
    }
    this.source = ctx.createMediaStreamSource(this.micStream)

    // Un nodo sólo se procesa si está enchufado a algo. Va a un gain a cero:
    // conectarlo al destino directamente arriesga oír la propia voz con eco.
    this.sink = ctx.createGain()
    this.sink.gain.value = 0
    this.sink.connect(ctx.destination)

    const listo = await this.setupWorklet(ctx)
    // Colgar mientras se cargaba el módulo: no montar nada sobre un contexto muerto.
    if (this.inputCtx !== ctx) return
    if (!listo) this.setupLegacyProcessor(ctx)
  }

  /** Captura moderna. Devuelve false si el navegador no la soporta. */
  private async setupWorklet(ctx: AudioContext): Promise<boolean> {
    if (!ctx.audioWorklet) return false

    try {
      this.workletUrl = URL.createObjectURL(new Blob([CAPTURE_WORKLET], { type: 'application/javascript' }))
      await ctx.audioWorklet.addModule(this.workletUrl)
      if (this.inputCtx !== ctx) return true

      const node = new AudioWorkletNode(ctx, 'pcm-capture', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        channelCount: 1,
        processorOptions: { chunkSize: CAPTURE_CHUNK }
      })
      node.port.onmessage = e => this.sendChunk(e.data as Float32Array)

      this.source?.connect(node)
      node.connect(this.sink as GainNode)
      this.capture = node
      return true
    } catch {
      // Contexto no seguro, CSP que bloquea el blob, navegador viejo... En
      // cualquiera de esos casos es mejor una llamada con el nodo deprecado que
      // ninguna llamada.
      return false
    }
  }

  /** Respaldo para navegadores sin AudioWorklet. */
  private setupLegacyProcessor(ctx: AudioContext) {
    this.legacyProcessor = ctx.createScriptProcessor(4096, 1, 1)
    this.legacyProcessor.onaudioprocess = e => this.sendChunk(e.inputBuffer.getChannelData(0))
    this.source?.connect(this.legacyProcessor)
    this.legacyProcessor.connect(this.sink as GainNode)
  }

  private handleMessage(raw: string) {
    let msg: Record<string, unknown>
    try {
      msg = JSON.parse(raw)
    } catch {
      return
    }

    switch (msg.type) {
      case 'ready':
        this.setStatus('live')
        break
      case 'audio':
        this.enqueueAudio(String(msg.data), typeof msg.format === 'string' ? msg.format : undefined)
        break
      case 'transcript':
        this.events.onTranscript?.(msg.role === 'user' ? 'user' : 'assistant', String(msg.text))
        break
      case 'interrupted':
        // El usuario habló encima: se tira todo lo que quedaba por sonar.
        this.stopPlayback()
        break
      case 'error':
        this.setStatus('error', String(msg.message))
        break
    }
  }

  /** Programa un trozo de audio del agente justo detrás del anterior. */
  private enqueueAudio(b64: string, format?: string) {
    // Normalmente ya existe (se crea al empezar la llamada); esto es la red por
    // si llegara audio antes de tiempo.
    if (!this.outputCtx) this.outputCtx = new AudioContext({ sampleRate: OUTPUT_SAMPLE_RATE })
    const ctx = this.outputCtx
    // Los navegadores pueden volver a suspenderlo (cambio de pestaña, corte de
    // salida). Sin esto, la llamada sigue "viva" pero muda.
    if (ctx.state === 'suspended') void this.resumeContext(ctx)

    const pcm = base64ToInt16(b64)
    // La frecuencia la dice el servidor en el `format`. Antes estaba fija a 24
    // kHz: un modelo que emita a otra frecuencia se oiría acelerado o lento.
    const rate = parseRate(format) ?? OUTPUT_SAMPLE_RATE
    const buffer = ctx.createBuffer(1, pcm.length, rate)
    const channel = buffer.getChannelData(0)
    for (let i = 0; i < pcm.length; i++) channel[i] = pcm[i] / 0x8000

    const node = ctx.createBufferSource()
    node.buffer = buffer
    node.connect(ctx.destination)

    const now = ctx.currentTime
    const startAt = Math.max(now, this.playhead)
    node.start(startAt)
    this.playhead = startAt + buffer.duration

    if (this.scheduled.length === 0) this.events.onSpeakingChange?.(true)
    this.scheduled.push(node)
    node.onended = () => {
      this.scheduled = this.scheduled.filter(n => n !== node)
      if (this.scheduled.length === 0) this.events.onSpeakingChange?.(false)
    }
  }

  private stopPlayback() {
    for (const node of this.scheduled) {
      try {
        node.stop()
      } catch {
        /* ya podía haber terminado */
      }
    }
    this.scheduled = []
    this.playhead = this.outputCtx?.currentTime ?? 0
    this.events.onSpeakingChange?.(false)
  }

  private teardownAudio() {
    if (this.capture) {
      // Cerrar el puerto: si no, un mensaje en vuelo llegaría con el socket ya
      // cerrado y `sendChunk` intentaría escribir en él.
      this.capture.port.onmessage = null
      this.capture.port.close()
      this.capture.disconnect()
    }
    this.legacyProcessor?.disconnect()
    this.sink?.disconnect()
    this.source?.disconnect()
    this.inputCtx?.close().catch(() => {})
    if (this.workletUrl) URL.revokeObjectURL(this.workletUrl)
    for (const t of this.micStream?.getTracks() ?? []) t.stop()
    this.stopPlayback()
    this.outputCtx?.close().catch(() => {})
    this.capture = undefined
    this.legacyProcessor = undefined
    this.workletUrl = undefined
    this.sink = undefined
    this.source = undefined
    this.inputCtx = undefined
    this.outputCtx = undefined
    this.micStream = undefined
  }

  /** Corta la llamada: avisa al backend, cierra el socket y suelta el micro. */
  hangup() {
    try {
      if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ type: 'end' }))
      this.ws?.close()
    } catch {
      /* cerrar es best-effort */
    }
    this.teardownAudio()
    this.setStatus('ended')
  }
}
